import React from "react";
import { If, Then } from "react-if";

/**
 * Renders a Kendo select value with optional image/icon alongside the text field.
 * Useful for custom dropdown value rendering across forms (e.g., showing a logo next to a tenant name).
 *
 * @template TValue - Object containing at least `id` and `name` plus any other fields used for display.
 * @param element - The default span element provided by Kendo that will be cloned.
 * @param textField - The object key on `value` whose string content should be displayed.
 * @param value - The selected option object used to derive display text and optional image.
 * @param imageRender - Optional renderer that returns a React node (e.g., avatar/icon) for the current value.
 * @returns A cloned span element with custom children combining the optional image and display text.
 * @example
 * const valueRender = (element, value) =>
 *   FormSelectValueRender(element, "name", value, v => <Avatar src={v.logoUrl} />);
 */
const FormSelectValueRender = <
  TValue extends Record<string, unknown> & { id: string; name: string },
>(
  element: React.ReactElement<HTMLSpanElement>,
  textField: string,
  value: TValue,
  imageRender?: (value: TValue) => React.ReactNode,
) => {
  if (!value) {
    return element;
  }
  const valueString = value?.[textField] as string;

  const img = imageRender ? imageRender(value) : undefined;

  const children = [
    <div style={{ display: "flex" }} key={valueString}>
      <If condition={!!img}>
        <Then>{img}</Then>
      </If>
      <span>{valueString}</span>
    </div>,
  ];

  return React.cloneElement(element, { ...element.props }, children);
};

export default FormSelectValueRender;
